import { a as trackSegmentEvent, i as resetSegmentUser, n as identifySegmentUser, r as logSegmentPageView } from "./segment-CWTqteLr.js";
import { l as usePreferredLocale } from "./Locale-Du9Jb0W-.js";
import { u as isDefined } from "./Utils-B1sWMmqZ.js";
import constate from "constate";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { pickBy } from "lodash-es";
import { useLocation } from "react-router-dom";
//#region src/Analytics/MultiTabDetector/multiTabDetector.ts
var EVENT_NEW_TAB = "newTabOpened";
var CHANNEL_NAME = "color-multi-tab-detector";
var globalMultiTabState = { hasMultipleTabs: false };
var updateGlobalMultiTabState = (hasMultipleTabs) => {
	globalMultiTabState.hasMultipleTabs = hasMultipleTabs;
};
function useMultiTabDetector() {
	const [hasMultipleTabs, setHasMultipleTabs] = useState(false);
	const broadcastChannelRef = useRef(null);
	const initializeBroadcastChannel = useCallback(() => {
		try {
			if (typeof BroadcastChannel === "undefined") return;
			const channel = new BroadcastChannel(CHANNEL_NAME);
			broadcastChannelRef.current = channel;
			channel.onmessage = (event) => {
				if (event.data === EVENT_NEW_TAB) {
					setHasMultipleTabs(true);
					updateGlobalMultiTabState(true);
				}
			};
			channel.postMessage(EVENT_NEW_TAB);
		} catch {}
	}, []);
	const destroy = useCallback(() => {
		if (broadcastChannelRef.current) {
			broadcastChannelRef.current.close();
			broadcastChannelRef.current = null;
		}
	}, []);
	const reset = useCallback(() => {
		setHasMultipleTabs(false);
		updateGlobalMultiTabState(false);
	}, []);
	useEffect(() => {
		initializeBroadcastChannel();
		return destroy;
	}, [initializeBroadcastChannel, destroy]);
	return {
		hasMultipleTabs,
		reset
	};
}
var dest$1 = constate(useMultiTabDetector);
var MultiTabDetectorProvider = dest$1[0];
dest$1[1];
var hasMultipleTabsOpen = () => {
	return globalMultiTabState.hasMultipleTabs;
};
//#endregion
//#region src/Analytics/actions.ts
/**
* Tracks an event with all web analytics tools we use.
* Wraps call in a 0-length timeout to force event logging
* to occur after all rendering/loading has completed.
* @param event The name of the event
* @param properties Supporting event parameters (can be anything)
*/
var trackAnalyticsEvent = (event, properties = {}) => {
	setTimeout(() => {
		trackSegmentEvent(event, {
			...properties,
			...hasMultipleTabsOpen() && { multiple_tabs_open: true }
		});
	}, 0);
};
/**
* Identifies a previously unknown (logged-out) user for analytics purposes.
* Wraps call in a 0-length timeout to force event logging
* to occur after all rendering/loading has completed.
* @param patientId The patient ID of the user to identify
* @param isColorEmployee Define if the user is Color employee or not. This information is used to
* set Segment traits.
*/
var identifyAnalyticsUser = (props) => {
	setTimeout(() => {
		identifySegmentUser(props);
	}, 0);
};
/**
* Clear user data for analytics purposes.
* Wraps call in a 0-length timeout to force event logging
* to occur after all rendering/loading has completed.
*/
var resetAnalyticsUser = () => {
	setTimeout(() => {
		resetSegmentUser();
	}, 0);
};
/**
* Registers with analytics providers that the user has seen a page.
* Wraps call in a 0-length timeout to force event logging
* to occur after all rendering/loading has completed.
* @param name Optional name for the page
* @param properties Extra properties to attach to the log
*/
var logAnalyticsPageView = (name, properties) => {
	setTimeout(() => {
		logSegmentPageView(name, {
			...properties,
			...hasMultipleTabsOpen() && { multiple_tabs_open: true }
		});
	}, 0);
};
//#endregion
//#region src/Analytics/Hooks/useReferrerContext/useReferrerContext.ts
/**
* Context hook to keep track of the previous visited page and use it as the referrer.
*
* <p>This is used to know how the users are navigating between the SPA pages. The first referrer
* is actually taken from {@code document.referrer}, but subsequent ones are the internal paths used
* in the app.
*/
function useReferrer() {
	const { pathname, search } = useLocation();
	const path = `${pathname}${search}`;
	const currentPathRef = useRef(document.referrer);
	const previousPathRef = useRef("");
	useLayoutEffect(() => {
		previousPathRef.current = currentPathRef.current;
		currentPathRef.current = path;
	}, [path]);
	return previousPathRef;
}
var dest = constate(useReferrer);
var ReferrerProvider = dest[0];
var useReferrerContext = dest[1];
//#endregion
//#region src/Analytics/Hooks/useAnalytics/useAnalytics.ts
/**
* ******************************************************************
* NOTE: This file is a duplicate of another file located at:
* projects/home/frontend/src/ui/common/hooks/useAnalytics/useAnalytics.ts
* Any changes made to this file should also be made to the original
* file to maintain consistency and avoid code duplication issues.
*
* This duplication was done instead of a complete move to avoid breaking tests.
* To resolve this, we would need to create higher-level mocks (at the @color/lib level)
* and refactor many tests, which requires a significant amount of time. For more details
* and future resolution, see the Jira ticket: https://getcolor.atlassian.net/browse/FG-63
* ******************************************************************
*/
/**
* Hook to provide event tracking analytics to various providers.
*/
var useAnalytics = () => {
	const referrerRef = useReferrerContext();
	const { locale } = usePreferredLocale();
	return { dangerouslyLogPageViewAnalytics: useCallback((name, properties) => {
		const referrer = referrerRef.current;
		logAnalyticsPageView(name, {
			preferred_locale: locale,
			...pickBy(properties, (value) => isDefined(value)),
			referrer
		});
	}, [referrerRef, locale]) };
};
/**
* Logs a page view for analytics using the uniquePageId passed.
*
* Prerequisites for logging:
*   `uniquePageId`, `pageName` are truthy
*   `loggingIsEnabled` is true.
*
* This hook caches the uniquePageId and, once the prerequisites are met, logs
* one time, until the uniquePageId changes.
*/
function useLogPageViewUsingUniqueId(props) {
	const { pathname } = useLocation();
	const { loggingIsEnabled = true, uniquePageId = pathname, pageName, properties } = props;
	const { dangerouslyLogPageViewAnalytics } = useAnalytics();
	const lastLoggedUniquePageIdRef = useRef("");
	/**
	* `useEffect` is often dangerous and unnecessary. This usage needs review by #ux-programs team.
	* See https://getcolor.atlassian.net/browse/UXTASK-1406
	*/
	useEffect(() => {
		if (loggingIsEnabled && uniquePageId && pageName && lastLoggedUniquePageIdRef.current !== uniquePageId) {
			lastLoggedUniquePageIdRef.current = uniquePageId;
			dangerouslyLogPageViewAnalytics(pageName, properties);
		}
	}, [
		loggingIsEnabled,
		dangerouslyLogPageViewAnalytics,
		pageName,
		properties,
		uniquePageId
	]);
}
function useIdentifyAnalyticsUser(userProps) {
	useEffect(() => {
		if (userProps) identifyAnalyticsUser(userProps);
	}, [userProps]);
}
//#endregion
export { useReferrerContext as a, resetAnalyticsUser as c, ReferrerProvider as i, trackAnalyticsEvent as l, useIdentifyAnalyticsUser as n, identifyAnalyticsUser as o, useLogPageViewUsingUniqueId as r, logAnalyticsPageView as s, useAnalytics as t, MultiTabDetectorProvider as u };

//# sourceMappingURL=Analytics-CHM4AGD9.js.map